'use client';
import * as React from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import siteConfig from '@/config/site';
import { useAvailability } from '@/hooks/useAvailability';
import type { OpeningHour } from '@/lib/types';

/**
 * Public booking form. Pass the effective opening hours (see resolveHours) so
 * the slots reflect any live override the owner made; falls back to config.
 *
 * Note: slot datetimes are composed in the browser's local timezone. Fine when
 * business and visitors share a timezone; make it explicit otherwise.
 */
export default function BookingForm({ hours = siteConfig.openingHours }: { hours?: OpeningHour[] }) {
  const openDays = React.useMemo(() => new Set(hours.map((h) => h.day)), [hours]);

  const dates = React.useMemo(() => {
    const out: { iso: string; label: string }[] = [];
    const today = new Date();
    for (let i = 0; i < siteConfig.booking.horizonDays; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      if (!openDays.has(d.getDay())) continue;
      out.push({
        iso: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString('fr-FR', { weekday: 'short', month: 'short', day: 'numeric' }),
      });
    }
    return out;
  }, [openDays]);

  const [service, setService] = React.useState(siteConfig.booking.services[0] ?? '');
  const [date, setDate] = React.useState(dates[0]?.iso ?? '');
  const [slot, setSlot] = React.useState('');
  const [form, setForm] = React.useState({ customerName: '', customerEmail: '', customerPhone: '', notes: '' });
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'done'>('idle');
  const [error, setError] = React.useState<string | null>(null);

  const { takenTimes, loading: loadingSlots } = useAvailability(date);

  React.useEffect(() => setSlot(''), [date]);

  const allSlots = React.useMemo(() => {
    const day = new Date(`${date}T00:00:00`).getDay();
    const step = siteConfig.booking.slotMinutes;
    // A day can have several ranges (split hours); generate slots inside each so
    // the gap between them (e.g. lunch) stays unbookable. Sort + dedupe in case
    // ranges are out of order or overlap.
    const slots = new Set<string>();
    for (const h of hours.filter((x) => x.day === day)) {
      const [oh, om] = h.open.split(':').map(Number);
      const [ch, cm] = h.close.split(':').map(Number);
      for (let m = oh * 60 + om; m + step <= ch * 60 + cm; m += step) {
        slots.add(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
      }
    }
    return [...slots].sort();
  }, [date, hours]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!slot) {
      setError('Veuillez choisir un créneau horaire.');
      return;
    }
    setStatus('submitting');
    const start = new Date(`${date}T${slot}:00`).toISOString();
    const res = await fetch('/api/appointments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, service, start }),
    });
    if (res.ok) {
      setStatus('done');
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body.error ?? 'Une erreur est survenue. Veuillez réessayer.');
      setStatus('idle');
    }
  }

  if (status === 'done') {
    return (
      <Alert severity="success">
        Merci {form.customerName} ! Votre demande pour <strong>{service}</strong> le{' '}
        {new Date(`${date}T${slot}`).toLocaleString('fr-FR')} a bien été reçue. Nous vous confirmerons très vite.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={3}>
        <TextField select label="Service" value={service} onChange={(e) => setService(e.target.value)} required>
          {siteConfig.booking.services.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>

        <TextField select label="Date" value={date} onChange={(e) => setDate(e.target.value)} required>
          {dates.map((d) => (
            <MenuItem key={d.iso} value={d.iso}>{d.label}</MenuItem>
          ))}
        </TextField>

        <Box>
          <Typography variant="subtitle2" gutterBottom>Créneaux disponibles</Typography>
          {loadingSlots ? (
            <CircularProgress size={24} />
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {allSlots.length === 0 && <Typography color="text.secondary" variant="body2">Fermé ce jour-là.</Typography>}
              {allSlots.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  color={slot === t ? 'primary' : 'default'}
                  variant={slot === t ? 'filled' : 'outlined'}
                  disabled={takenTimes.has(t)}
                  onClick={() => setSlot(t)}
                />
              ))}
            </Box>
          )}
        </Box>

        <TextField label="Votre nom" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} required />
        <TextField label="E-mail" type="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} required />
        <TextField label="Téléphone" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} required />
        <TextField label="Remarques (facultatif)" multiline rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

        {error && <Alert severity="error">{error}</Alert>}

        <Button type="submit" variant="contained" size="large" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Envoi…' : 'Demander un rendez-vous'}
        </Button>
      </Stack>
    </Box>
  );
}
